import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ArrowUpTrayIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { toast } from 'sonner'
import Button from '../ui/Button'
import ConfirmDialog from '../ui/ConfirmDialog'
import ImageFullscreenViewer from '../ui/ImageFullscreenViewer'
import SingleSelect from '../ui/SingleSelect'
import ZoomableImage from '../ui/ZoomableImage'
import { useCatalog } from '../../contexts/CatalogContext'
import {
  createBevelGeometryImageUploadUrl,
  deleteBevelGeometryImage,
  listBevelGeometryImagesAdmin,
  registerBevelGeometryImage,
  type BevelGeometryImageAdminItem,
  type BevelParameterLabels,
  type ParameterLabelKey
} from '../../utils/bevelGeometryImagesClient'
import { putFileToPresignedUrl } from '../../utils/reportsClient'

const PARAMETER_LABEL_KEYS: ParameterLabelKey[] = [
  'parameter1',
  'parameter2',
  'parameter3',
  'parameter4',
  'parameter5',
  'parameter6',
  'parameter7',
  'parameter8',
  'parameter9',
  'parameter10',
  'parameter11',
  'parameter12',
  'parameter13'
]

const PAGE_SIZE = 50

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB'
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

function formatDate(iso: string): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString()
  } catch {
    return iso
  }
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s
  return `${s.slice(0, max)}…`
}

const emptyParams = () => Array.from({ length: 13 }, () => '')
const emptyLabels = (): BevelParameterLabels => ({})

function bevelKeysMatchRow(
  row: BevelGeometryImageAdminItem,
  jointType: string,
  parameters: string[],
  extraData: string
): boolean {
  if (row.jointType !== jointType.trim()) return false
  for (let i = 0; i < 13; i += 1) {
    const key = `parameter${i + 1}` as keyof BevelGeometryImageAdminItem
    if (String(row[key] ?? '') !== parameters[i]) return false
  }
  return (row.extraData || '') === extraData.trim()
}

function normalizeLabels(input: BevelParameterLabels | null | undefined): BevelParameterLabels {
  if (!input) return {}
  const out: BevelParameterLabels = {}
  for (const k of PARAMETER_LABEL_KEYS) {
    const v = input[k]
    if (typeof v === 'string' && v.trim().length > 0) out[k] = v.trim()
  }
  return out
}

type BevelModalProps = {
  open: boolean
  mode: 'create' | 'edit'
  editRow: BevelGeometryImageAdminItem | null
  onClose: () => void
  onSaved: () => void
}

function BevelGeometryImageFormModal({ open, mode, editRow, onClose, onSaved }: BevelModalProps) {
  const { getLabelsMerged, addLabel } = useCatalog()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  const [jointType, setJointType] = useState('')
  const [parameters, setParameters] = useState<string[]>(() => emptyParams())
  const [labels, setLabels] = useState<BevelParameterLabels>(() => emptyLabels())
  const [extraData, setExtraData] = useState('')
  const [uploading, setUploading] = useState(false)

  const jointTypeOptions = getLabelsMerged(
    'bevel_joint_type',
    editRow?.jointType ? [editRow.jointType] : []
  )
  const labelOptions = getLabelsMerged('bevel_param_label', Object.values(labels).filter(Boolean) as string[])

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && editRow) {
      setJointType(editRow.jointType)
      setParameters([
        editRow.parameter1,
        editRow.parameter2,
        editRow.parameter3,
        editRow.parameter4,
        editRow.parameter5,
        editRow.parameter6,
        editRow.parameter7,
        editRow.parameter8,
        editRow.parameter9,
        editRow.parameter10,
        editRow.parameter11,
        editRow.parameter12,
        editRow.parameter13
      ])
      setExtraData(editRow.extraData || '')
      setLabels(normalizeLabels(editRow.parameterLabels))
    } else {
      setJointType('')
      setParameters(emptyParams())
      setExtraData('')
      setLabels(emptyLabels())
    }
    setFile(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [open, mode, editRow])

  useEffect(() => {
    if (!file) {
      setFilePreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(file)
    setFilePreviewUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [file])

  const jointTypeTrimmed = jointType.trim()
  const canReuseImage = mode === 'edit' && editRow && !!editRow.fileKey
  const canSubmit =
    !uploading &&
    jointTypeTrimmed.length > 0 &&
    (mode === 'create' ? !!file : !!file || canReuseImage)

  const previewLabel = useMemo(() => {
    const extra = extraData.trim() ? ' + JSON' : ''
    return `${jointTypeTrimmed || 'No type'} / P1–P13${extra}`
  }, [jointTypeTrimmed, extraData])

  const previewSrc =
    filePreviewUrl ||
    (mode === 'edit' && editRow?.signedImageUrl ? editRow.signedImageUrl : null)

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error('Enter joint type and parameters. Create requires a new image file.')
      return
    }
    setUploading(true)
    try {
      const [p1, p2, p3, p4, p5, p6, p7, p8, p9, p10, p11, p12, p13] = parameters
      let fileKey = ''
      let imageUrl = ''
      if (file) {
        const up = await createBevelGeometryImageUploadUrl({
          fileName: file.name,
          contentType: file.type || 'image/png'
        })
        await putFileToPresignedUrl(up.uploadUrl, file)
        fileKey = up.fileKey
        imageUrl = up.publicUrl
      } else if (canReuseImage && editRow) {
        fileKey = editRow.fileKey
        imageUrl = editRow.imageUrl || ''
      } else {
        toast.error('Select an image file.')
        setUploading(false)
        return
      }
      if (mode === 'edit' && editRow) {
        const keysMatch = bevelKeysMatchRow(editRow, jointTypeTrimmed, parameters, extraData)
        if (!keysMatch) {
          await deleteBevelGeometryImage(editRow.id)
        }
      }
      const normalizedLabels = normalizeLabels(labels)
      await registerBevelGeometryImage({
        jointType: jointTypeTrimmed,
        parameter1: p1,
        parameter2: p2,
        parameter3: p3,
        parameter4: p4,
        parameter5: p5,
        parameter6: p6,
        parameter7: p7,
        parameter8: p8,
        parameter9: p9,
        parameter10: p10,
        parameter11: p11,
        parameter12: p12,
        parameter13: p13,
        extraData: extraData.trim() ? extraData : undefined,
        imageUrl,
        fileKey,
        parameterLabels: Object.keys(normalizedLabels).length > 0 ? normalizedLabels : null
      })
      toast.success(mode === 'edit' ? 'Mapping updated.' : 'Mapping created.')
      onSaved()
      onClose()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to save mapping.')
    } finally {
      setUploading(false)
    }
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bevel-geom-modal-title"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-3">
          <h2 id="bevel-geom-modal-title" className="text-sm font-semibold text-gray-900">
            {mode === 'edit' ? 'Edit bevel geometry image' : 'Create bevel geometry image'}
          </h2>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="grid gap-4 xl:grid-cols-[40%_60%]">
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-800">Diagram image</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="sr-only"
              disabled={uploading}
              onChange={(event) => {
                setFile(event.target.files?.[0] ?? null)
              }}
            />
            {previewSrc ? (
              <div className="space-y-2">
                <div
                  className="h-64 w-full overflow-hidden rounded-xl border border-gray-200 bg-gray-50"
                  data-testid="bevel-image-preview"
                >
                  <ZoomableImage
                    src={previewSrc}
                    alt={`${jointTypeTrimmed || 'Bevel'} diagram preview`}
                    className="h-full w-full"
                  />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
                  <span className="truncate" title={file?.name}>
                    {file ? `New: ${file.name} (${formatFileSize(file.size)})` : 'Current image'}
                  </span>
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-md border border-gray-300 px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {file ? 'Choose a different file' : 'Replace image'}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 px-6 py-12 text-center transition hover:border-primary-300 hover:bg-primary-50/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <ArrowUpTrayIcon className="h-7 w-7 text-gray-400" />
                <span className="mt-2 text-xs font-medium text-gray-700">Select diagram image</span>
              </button>
            )}
          </div>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium text-gray-800">Joint / bevel type</label>
              <SingleSelect
                value={jointType}
                options={jointTypeOptions}
                placeholder="Select bevel type..."
                addLabel="Add Bevel Type"
                createModalTitle="Add bevel type"
                className="mt-1"
                interactionLocked={uploading}
                onChange={(v) => setJointType(v)}
                onCreateOption={async (v) => {
                  await addLabel('bevel_joint_type', v)
                  setJointType(v)
                }}
              />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800">Parameters</p>
              <p className="mt-0.5 text-[11px] text-gray-500">
                For each slot pick a parameter name from the dropdown and enter the value used in the diagram.
              </p>
              <div className="mt-2 grid max-h-[600px] gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                {parameters.map((value, i) => {
                  const key = PARAMETER_LABEL_KEYS[i]
                  return (
                    <div
                      key={key}
                      className="rounded-md border border-gray-200 bg-white p-2"
                      data-testid={`bevel-param-row-${i + 1}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] font-semibold text-gray-700">P{i + 1}</span>
                      </div>
                      <SingleSelect
                        value={labels[key] ?? ''}
                        options={labelOptions}
                        placeholder="Parameter name…"
                        addLabel="Add Parameter Name"
                        createModalTitle="Add parameter name"
                        createFieldLabel="Parameter name"
                        createInputPlaceholder="e.g. Fill angle (A)"
                        className="mt-1"
                        interactionLocked={uploading}
                        onChange={(v) => {
                          setLabels((prev) => {
                            const next = { ...prev }
                            if (v) next[key] = v
                            else delete next[key]
                            return next
                          })
                        }}
                        onCreateOption={async (v) => {
                          await addLabel('bevel_param_label', v)
                          setLabels((prev) => ({ ...prev, [key]: v }))
                        }}
                      />
                      <input
                        className="input-primary mt-2 h-8 text-xs"
                        value={value}
                        disabled={uploading}
                        placeholder="Value"
                        aria-label={labels[key] ? `${labels[key]} value` : `Parameter ${i + 1} value`}
                        onChange={(e) => {
                          const next = [...parameters]
                          next[i] = e.target.value
                          setParameters(next)
                        }}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-800">Extra data (JSON)</label>
              <textarea
                className="input-primary mt-1 min-h-[72px] w-full resize-y font-mono text-xs"
                value={extraData}
                disabled={uploading}
                placeholder='e.g. {"rootGap":"2mm"}'
                onChange={(e) => setExtraData(e.target.value)}
              />
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-2">
              <p className="text-[11px] font-medium text-gray-600">Preview</p>
              <p className="mt-0.5 text-xs text-gray-800">{previewLabel}</p>
            </div>
          </div>
        </div>
        </div>
        <div className="flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-white px-4 py-3">
          <Button variant="secondary" type="button" disabled={uploading} onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {uploading ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  )
}

function paramsSummary(row: BevelGeometryImageAdminItem): string {
  const parts = [
    row.parameter1,
    row.parameter2,
    row.parameter3,
    row.parameter4,
    row.parameter5,
    row.parameter6,
    row.parameter7,
    row.parameter8,
    row.parameter9,
    row.parameter10,
    row.parameter11,
    row.parameter12,
    row.parameter13
  ].filter(Boolean)
  if (parts.length === 0) return '—'
  return truncate(parts.join(', '), 48)
}

export default function BevelGeometryImagesAdmin() {
  const { getLabels } = useCatalog()
  const filterJointTypeOptions = getLabels('bevel_joint_type')
  const [items, setItems] = useState<BevelGeometryImageAdminItem[]>([])
  const [total, setTotal] = useState(0)
  const [listLoading, setListLoading] = useState(true)
  const [jointTypeInput, setJointTypeInput] = useState('')
  const [jointTypeApplied, setJointTypeApplied] = useState('')
  const [loadingMore, setLoadingMore] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const [editRow, setEditRow] = useState<BevelGeometryImageAdminItem | null>(null)
  const [fullscreen, setFullscreen] = useState<{ url: string; title: string } | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BevelGeometryImageAdminItem | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const loadFirst = useCallback(async () => {
    setListLoading(true)
    try {
      const res = await listBevelGeometryImagesAdmin({
        limit: PAGE_SIZE,
        offset: 0,
        jointType: jointTypeApplied || undefined
      })
      setTotal(res.total)
      setItems(res.items)
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load mappings.')
    } finally {
      setListLoading(false)
    }
  }, [jointTypeApplied])

  useEffect(() => {
    void loadFirst()
  }, [loadFirst])

  const canLoadMore = items.length < total

  const openCreate = () => {
    setModalMode('create')
    setEditRow(null)
    setModalOpen(true)
  }

  const openEdit = (row: BevelGeometryImageAdminItem) => {
    setModalMode('edit')
    setEditRow(row)
    setModalOpen(true)
  }

  const confirmDelete = async () => {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      await deleteBevelGeometryImage(deleteTarget.id)
      toast.success('Mapping deleted.')
      setDeleteTarget(null)
      void loadFirst()
    } catch (err: any) {
      toast.error(err?.message || 'Delete failed.')
    } finally {
      setDeleteBusy(false)
    }
  }

  const refreshAfterSave = () => void loadFirst()

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Bevel geometry image mappings</h3>
          {/* <p className="mt-0.5 text-xs text-gray-500">
            Joint type + 13 parameters + optional JSON. Filter by joint type; edit can keep the current image file.
          </p> */}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" type="button" disabled={listLoading} onClick={() => void loadFirst()}>
            Refresh
          </Button>
          <Button type="button" onClick={openCreate}>
            Create
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-gray-50/90 p-3">
        <div className="min-w-[200px] flex-1">
          <label className="block text-xs font-medium text-gray-700">Joint type contains</label>
          <input
            className="input-primary mt-1 text-sm"
            value={jointTypeInput}
            onChange={(e) => setJointTypeInput(e.target.value)}
            placeholder="e.g. Single V"
            list="bevel-admin-joint-filter-presets"
          />
          <datalist id="bevel-admin-joint-filter-presets">
            {filterJointTypeOptions.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>
        <Button
          type="button"
          className="mb-0.5"
          onClick={() => {
            setJointTypeApplied(jointTypeInput.trim())
          }}
        >
          Apply filter
        </Button>
        <Button
          variant="secondary"
          type="button"
          className="mb-0.5"
          onClick={() => {
            setJointTypeInput('')
            setJointTypeApplied('')
          }}
        >
          Clear filter
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="sticky top-0 bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Image</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Joint type</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Params</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Extra</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Updated</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {listLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-xs text-gray-500">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-xs text-gray-500">
                  No mappings yet. Use Create to add one.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row.id}>
                  <td className="px-3 py-2">
                    {row.signedImageUrl ? (
                      <button
                        type="button"
                        className="block h-14 w-14 overflow-hidden rounded-md border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        onClick={() =>
                          setFullscreen({
                            url: row.signedImageUrl!,
                            title: row.jointType
                          })
                        }
                      >
                        <img src={row.signedImageUrl} alt="" className="h-full w-full object-cover" />
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">No preview</span>
                    )}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-2 text-gray-800" title={row.jointType}>
                    {row.jointType}
                  </td>
                  <td className="max-w-[180px] px-3 py-2 text-xs text-gray-600" title={paramsSummary(row)}>
                    {paramsSummary(row)}
                  </td>
                  <td className="max-w-[140px] truncate px-3 py-2 text-xs text-gray-600" title={row.extraData || ''}>
                    {row.extraData ? truncate(row.extraData, 40) : '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-600">{formatDate(row.updatedAt)}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-right">
                    <button
                      type="button"
                      className="mr-2 inline-flex rounded p-1 text-primary-600 hover:bg-primary-50"
                      title="Edit"
                      onClick={() => openEdit(row)}
                    >
                      <PencilSquareIcon className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      className="inline-flex rounded p-1 text-red-600 hover:bg-red-50"
                      title="Delete"
                      onClick={() => setDeleteTarget(row)}
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {canLoadMore && (
        <div className="flex justify-center">
          <Button
            variant="secondary"
            type="button"
            disabled={loadingMore}
            onClick={() => {
              void (async () => {
                setLoadingMore(true)
                try {
                  const res = await listBevelGeometryImagesAdmin({
                    limit: PAGE_SIZE,
                    offset: items.length,
                    jointType: jointTypeApplied || undefined
                  })
                  setTotal(res.total)
                  setItems((prev) => [...prev, ...res.items])
                } catch (err: any) {
                  toast.error(err?.message || 'Failed to load more.')
                } finally {
                  setLoadingMore(false)
                }
              })()
            }}
          >
            {loadingMore ? 'Loading…' : `Load more (${items.length} of ${total})`}
          </Button>
        </div>
      )}

      <BevelGeometryImageFormModal
        open={modalOpen}
        mode={modalMode}
        editRow={editRow}
        onClose={() => setModalOpen(false)}
        onSaved={refreshAfterSave}
      />

      <ImageFullscreenViewer
        open={!!fullscreen}
        url={fullscreen?.url ?? null}
        title={fullscreen?.title}
        onClose={() => setFullscreen(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete mapping?"
        description={
          deleteTarget
            ? `Remove diagram for joint type "${deleteTarget.jointType}"? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        tone="danger"
        confirmBusy={deleteBusy}
        onCancel={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  )
}
